import { useState } from "react";
import { ScanLine, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { submitBarcode } from "../lib/api";

export default function BarcodeSubmissionCTA({ productId }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // UPC/EAN: 8, 12, 13, or 14 digits.
  const digits = code.replace(/\D+/g, "");
  const valid = [8, 12, 13, 14].includes(digits.length);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await submitBarcode(productId, {
        barcode: digits,
        reporter_email: email.trim() || undefined,
      });
      setDone(true);
      toast.success("Thanks \u2014 we'll verify and add it", {
        description: "Once approved, this barcode will be scannable for every Blendsafe user.",
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "We couldn't save that submission just now.";
      toast.error("Submission couldn't be saved", {
        description: typeof msg === "string" ? msg : "Check your connection and try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section
        data-testid="barcode-submission-thanks"
        className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 sm:p-6 flex items-start gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h2 className="text-[15px] font-bold text-emerald-900">
            Submitted for review
          </h2>
          <p className="text-[13px] text-emerald-800/90 leading-relaxed">
            A reviewer will verify the barcode. Once approved, this product
            will be scannable for everyone.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="barcode-submission-cta"
      className="rounded-2xl bg-cream-100/70 border border-surface-line p-5 sm:p-6 space-y-3.5"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-surface-line shrink-0">
          <ScanLine className="w-4.5 h-4.5 text-ink-soft" />
        </span>
        <div className="space-y-1">
          <h2 className="text-[15px] font-bold text-ink">
            Have this product on hand?
          </h2>
          <p className="text-[13px] text-ink-soft leading-relaxed max-w-prose">
            Type the barcode from the package — we'll add it so the next person
            can just scan and check.
          </p>
        </div>
      </div>

      {!open && (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="barcode-submission-open"
          variant="outline"
          className="border-surface-line text-ink hover:bg-white"
          size="sm"
        >
          <ScanLine className="w-4 h-4 mr-1.5" />
          Submit barcode
        </Button>
      )}

      {open && (
        <div className="space-y-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute mb-1.5">
              Barcode (UPC / EAN)
            </span>
            <input
              data-testid="barcode-submission-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              maxLength={20}
              placeholder="e.g. 0769915190649"
              className="w-full rounded-xl border border-surface-line bg-white px-3.5 h-11 text-[14px] tracking-wider tabular-nums text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-200 focus:border-ink-mute"
            />
            <span
              data-testid="barcode-submission-helper"
              className={`block mt-1 text-[12px] ${valid ? "text-emerald-700" : "text-ink-mute"}`}
            >
              {valid
                ? `Looks good — ${digits.length}-digit code, ready to submit.`
                : `${digits.length} digits (need 8, 12, 13 or 14).`}
            </span>
          </label>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute mb-1.5">
              Email (optional)
            </span>
            <input
              data-testid="barcode-submission-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-surface-line bg-white px-3.5 h-11 text-[14px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-200 focus:border-ink-mute"
            />
            <span className="block mt-1 text-[12px] text-ink-mute">
              Only used if our reviewer needs to clarify something.
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              type="button"
              onClick={submit}
              disabled={!valid || busy}
              data-testid="barcode-submission-submit"
              className="bg-ink hover:bg-ink-soft text-cream-50"
              size="sm"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Submit for review
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="barcode-submission-cancel"
              className="text-[13px] font-semibold text-ink-mute hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
